// src/components/CatalogueRejections/CatalogueRejections.js

import React, { useState } from "react";
// ... (existing imports)

import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Info } from "lucide-react";

import CopyIcon from "../../components/CopyIcon/CopyIcon";
import Pagination from "../../components/Pagination/Pagination";
import AppLoader from "../../components/AppLoader/appLoader";
import { useCatalogueRejections } from "../../customHooks/CatalogueRejection/useCatalogueRejection";
import { Link } from "react-router-dom";
import { FaSortDown, FaSortUp } from "react-icons/fa";
// Note: Assuming the correct path to the hook is updated here if needed:

// Define column tooltips/descriptions (Placeholder created based on table structure)
const columnInfo = {
    messageId: "The unique ID assigned to the catalogue submission message.",
    transactionId: "The Transaction ID associated with the catalogue submission.",
    bapId: "The ID of the Buyer Application (BAP) that submitted the catalogue.",
    rejectionType: "The classification of the rejection (e.g., SCHEMA_ERROR, POLICY_VIOLATION).",
    statusCode: "The status code returned by the system for the catalogue processing.",
    error: "The first detailed error message explaining the reason for rejection.",
    resolvedStatus: "Indicates if the rejection has been internally followed up or marked as resolved.",
};

const CatalogueRejections = () => {
  const today = new Date().toISOString().slice(0, 10);
  const {
    data,
    loading,
    filters,
    setFilters,
    filterOptions,
    totalCount,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
  } = useCatalogueRejections();

  //   const canViewRejections = usePermission("CATALOG_REJECTION_LIST_VIEW");

  const getFirstError = (errors) => {
    if (Array.isArray(errors) && errors.length > 0) {
      return errors[0].message || "No message provided";
    }
    return "N/A";
  };

  // UPDATED: Use consistent defaults matching the hook
  const initialFilters = {
    city: "",
    domain: "",
    bap_id: "",
    catalog_status_code: "",
    catalog_rejection_type: "",
    created_from: "",
    created_to: "",
    search_by: "",
    search_value: "",
    sort_by: "createdAt", // Ensure this default is consistent
    sort_order: "DESC", // Ensure this default is consistent (Uppercase)
  };

  const formatLabel = (text) => {
    if (!text) return "N/A";
    // Simple format function (adjust this based on your imported formatLabel if needed)
    return text
      .toString()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const handleFilterChange = (key, value) => {
    setFilters((f) => ({
      ...f,
      [key]: value,
    }));
    setCurrentPage(1);
  };

  // Helper for sort logic
  const handleSort = (key) => {
    setFilters((f) => ({
      ...f,
      sort_by: key,
      // Toggle order: ASC -> DESC, DESC -> ASC
      sort_order: f.sort_order === "DESC" ? "ASC" : "DESC",
    }));
    setCurrentPage(1);
  };

  // Helper for displaying sort indicators
  const renderSortIndicator = (key) => {
    if (filters.sort_by === key) {
      // Using FaSortUp/Down for visual consistency with other tables (since they are imported via FaSort)
      return filters.sort_order === "ASC" ? (
        <span> <FaSortUp size={14} /> </span>
      ) : (
        <span> <FaSortDown size={14} /> </span>
      );
    }
    return null;
  };

  return (
    <>
      {/* {canViewRejections ? ( */}
      <div className="m-4">
        <p className="text-muted small mb-3">
          This page displays a list of all rejected catalog submissions. Use the
          filters to search for specific rejections. The **Resolved** status
          indicates internal follow-up and does not guarantee re-submission
          success.
        </p>

        {/* --- Filters Wrapper (Styled like Orders) --- */}
        <div className="filters-wrapper bg-white px-3 py-2 rounded shadow-sm mb-4">
          <div className="row gx-4 gy-3 align-items-end flex-wrap">
            {/* Search By (message_id, transaction_id, bap_id) */}
            <div className="col-auto">
              <label className="form-label mb-1">Search By</label>
              <div className="input-group" style={{ minWidth: "400px" }}>
                <select
                  className="form-select"
                  style={{ maxWidth: "150px" }}
                  value={filters.search_by}
                  onChange={(e) => {
                    setFilters((f) => ({
                      ...f,
                      search_by: e.target.value,
                      search_value: "",
                    }));
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Select Field</option>
                  <option value="message_id">Message ID</option>
                  <option value="transaction_id">Transaction ID</option>
                  <option value="bap_id">BAP ID</option>
                </select>

                {filters.search_by === "bap_id" ? (
                  <select
                    className="form-select"
                    style={{ fontSize: "12px" }}
                    value={filters.search_value}
                    onChange={(e) =>
                      handleFilterChange("search_value", e.target.value)
                    }
                  >
                    <option value="">Select BAP ID</option>
                    {filterOptions?.bap_id?.map((bap) => {
                      const value =
                        typeof bap === "object"
                          ? bap?.bap_id || bap?.subscriber_id || bap?.name
                          : bap;
                      const label =
                        typeof bap === "object"
                          ? bap?.name || bap?.bap_id || value
                          : bap;
                      return (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    style={{ fontSize: "12px", padding: "none" }}
                    placeholder={`Enter ${
                      filters.search_by
                        ? filters.search_by.replace("_", " ")
                        : "value"
                    }`}
                    value={filters.search_value}
                    onChange={(e) =>
                      handleFilterChange("search_value", e.target.value)
                    }
                    disabled={!filters.search_by}
                  />
                )}
              </div>
            </div>

            {/* Status Code */}
            <div className="col-auto">
              <label className="form-label mb-1">Status Code</label>
              <select
                className="form-select"
                value={filters.catalog_status_code}
                onChange={(e) =>
                  handleFilterChange("catalog_status_code", e.target.value)
                }
              >
                <option value="">All</option>
                {filterOptions?.catalog_status_code?.map((s) => (
                  <option key={s} value={s}>
                    {s
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            {/* Rejection Type */}
            <div className="col-auto">
              <label className="form-label mb-1">Rejection Type</label>
              <select
                className="form-select"
                value={filters.catalog_rejection_type}
                onChange={(e) =>
                  handleFilterChange("catalog_rejection_type", e.target.value)
                }
              >
                <option value="">All</option>
                {filterOptions?.catalog_rejection_type?.map((s) => (
                  <option key={s} value={s}>
                    {s
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            {/* Domain */}
            <div className="col-auto">
              <label className="form-label mb-1">Domain</label>
              <select
                className="form-select"
                style={{ minWidth: "160px" }}
                value={filters.domain}
                onChange={(e) => handleFilterChange("domain", e.target.value)}
              >
                <option value="">All</option>
                {filterOptions?.domain?.map((s) => {
                  const val =
                    typeof s === "object"
                      ? s?.domain || s?.name || s?.value || s
                      : s;
                  return (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Created From */}
            <div className="col-auto">
              <label className="form-label mb-1">Created From</label>
              <input
                type="date"
                className="form-control"
                style={{ fontSize: "12px", padding: "none" }}
                value={filters.created_from}
                onChange={(e) =>
                  handleFilterChange("created_from", e.target.value)
                }
                max={today}
              />
            </div>

            {/* Created To */}
            <div className="col-auto">
              <label className="form-label mb-1">Created To</label>
              <input
                type="date"
                className="form-control"
                style={{ fontSize: "12px", padding: "none" }}
                value={filters.created_to}
                onChange={(e) =>
                  handleFilterChange("created_to", e.target.value)
                }
                max={today}
                min={filters.created_from || ""}
              />
            </div>

            {/* Clear Filters Button - Styled like Orders Clear Filters button */}
            <div className="col-auto d-flex">
              <button
                type="button"
                className="btn btn-primary no-hover-btn" // Changed to primary style
                style={{ padding: "5px 8px" }}
                onClick={() => {
                  setFilters(initialFilters);
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* --- Table Section --- */}
        {loading ? (
          <AppLoader />
        ) : data && data.length > 0 ? (
          <div className="table-responsive mt-4">
            <table className="table table-bordered table-hover align-middle text-center">
              <thead className="table-light text-center">
                <tr>
                  {/* ID */}
                  <th>ID</th>
                  
                  {/* Message ID */}
                  <th>
                    Message ID
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{columnInfo.messageId}</Tooltip>}
                    >
                      <span style={{ marginLeft: "4px", cursor: "help" }}>
                        <Info size={14} className="text-muted" />
                      </span>
                    </OverlayTrigger>
                  </th>
                  
                  {/* Transaction ID */}
                  <th>
                    Transaction ID
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{columnInfo.transactionId}</Tooltip>}
                    >
                      <span style={{ marginLeft: "4px", cursor: "help" }}>
                        <Info size={14} className="text-muted" />
                      </span>
                    </OverlayTrigger>
                  </th>
                  
                  {/* BAP ID */}
                  <th>
                    BAP ID
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{columnInfo.bapId}</Tooltip>}
                    >
                      <span style={{ marginLeft: "4px", cursor: "help" }}>
                        <Info size={14} className="text-muted" />
                      </span>
                    </OverlayTrigger>
                  </th>
                  
                  {/* City */}
                  <th>City</th>
                  
                  {/* Domain */}
                  <th>Domain</th>
                  
                  {/* Type */}
                  <th>
                    Type
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{columnInfo.rejectionType}</Tooltip>}
                    >
                      <span style={{ marginLeft: "4px", cursor: "help" }}>
                        <Info size={14} className="text-muted" />
                      </span>
                    </OverlayTrigger>
                  </th>
                  
                  {/* Status Code */}
                  <th>
                    Status Code
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{columnInfo.statusCode}</Tooltip>}
                    >
                      <span style={{ marginLeft: "4px", cursor: "help" }}>
                        <Info size={14} className="text-muted" />
                      </span>
                    </OverlayTrigger>
                  </th>
                  
                  {/* First Error Message */}
                  <th style={{ minWidth: "200px" }}>
                    First Error Message
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{columnInfo.error}</Tooltip>}
                    >
                      <span style={{ marginLeft: "4px", cursor: "help" }}>
                        <Info size={14} className="text-muted" />
                      </span>
                    </OverlayTrigger>
                  </th>
                  
                  {/* Resolved */}
                  <th>
                    Resolved
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{columnInfo.resolvedStatus}</Tooltip>}
                    >
                      <span style={{ marginLeft: "4px", cursor: "help" }}>
                        <Info size={14} className="text-muted" />
                      </span>
                    </OverlayTrigger>
                  </th>
                  
                  {/* Created At (Sortable) - Removed fixed width */}
                  <th
                    className="cursor-pointer"
                    onClick={() => handleSort("createdAt")}
                  >
                    Created At {renderSortIndicator("createdAt")}
                  </th>
                  
                  {/* Updated At (Sortable) - Removed fixed width */}
                  <th
                    className="cursor-pointer"
                    onClick={() => handleSort("updated_at")}
                  >
                    Updated At {renderSortIndicator("updated_at")}
                  </th>
                  
                  {/* Remarks - Removed fixed width */}
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {data.map((rejection) => (
                    <tr key={rejection.id}>
                      {/* ID */}
                      <td>
                        <Link
                          to={`/catalogue-rejection/${rejection.id}`}
                          state={{ catalogue: rejection }} // <-- ADD THIS LINE
                          style={{ textDecoration: "none" }}
                          title={`View details for ID ${rejection.id}`}
                        >
                          {rejection.id}
                        </Link>
                      </td>
                      
                      {/* Message ID */}
                      <td>
                        <div className="copy-cell">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{rejection.message_id}</Tooltip>}
                          >
                            <span className="truncate-cell">
                              {rejection.message_id}
                            </span>
                          </OverlayTrigger>
                          <CopyIcon textToCopy={rejection.message_id} />
                        </div>
                      </td>
                      
                      {/* Transaction ID */}
                      <td>
                        <div className="copy-cell">
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>{rejection.transaction_id}</Tooltip>
                            }
                          >
                            <span className="truncate-cell">
                              {rejection.transaction_id}
                            </span>
                          </OverlayTrigger>
                          <CopyIcon textToCopy={rejection.transaction_id} />
                        </div>
                      </td>
                      
                      {/* BAP ID */}
                      <td>
                        <div className="copy-cell">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{rejection.bap_id}</Tooltip>}
                          >
                            <span className="truncate-cell">
                              {rejection.bap_id}
                            </span>
                          </OverlayTrigger>
                          <CopyIcon textToCopy={rejection.bap_id} />
                        </div>
                      </td>
                      
                      {/* City */}
                      <td>{rejection.city}</td>
                      
                      {/* Domain */}
                      <td>{rejection.domain}</td>
                      
                      {/* Type */}
                      <td>{formatLabel(rejection.catalog_rejection_type)}</td>
                      
                      {/* Status Code */}
                      <td>{formatLabel(rejection.catalog_status_code)}</td>
                      
                      {/* First Error Message */}
                      <td className="text-start">
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip style={{ maxWidth: "500px" }}>
                              {getFirstError(rejection.catalog_rejection_error)}
                            </Tooltip>
                          }
                        >
                          <span
                            className="truncate-cell"
                            style={{
                              maxWidth: "200px",
                              display: "inline-block",
                            }}
                          >
                            {getFirstError(rejection.catalog_rejection_error)}
                          </span>
                        </OverlayTrigger>
                      </td>
                      
                      {/* Resolved */}
                      <td>
                        {rejection.catalog_resolved_status ? "YES" : "NO"}
                      </td>
                      
                      {/* Created At */}
                      <td>{rejection.created_at}</td>
                      
                      {/* Updated At */}
                      <td>{rejection.updated_at}</td>
                      
                      {/* Remarks */}
                      <td>{rejection.remarks || "N/A"}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#9ca3af",
              fontSize: "14px",
              marginTop: "16px",
            }}
          >
            No Catalog Rejections Found
          </div>
        )}

        {/* --- Pagination --- */}
        {totalCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            startItem={(currentPage - 1) * itemsPerPage + 1}
            endItem={Math.min(currentPage * itemsPerPage, totalCount)}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
      {/* ) : (
        <UnAuthorized />
      )} */}
    </>
  );
};

export default CatalogueRejections;